import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';

import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { authService } from '../services/authService';
import { setGoogleCallbackFlow } from '../services/api';

const GoogleCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authenticateWithGoogle, authenticateWithToken } = useAuth();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const handleGoogleCallback = async () => {
      // Set flag to suppress 401 errors during Google callback flow
      setGoogleCallbackFlow(true);
      
      try {
        setLoading(true);
        
        // Check if there's an error from Google OAuth
        const errorParam = searchParams.get('error');
        if (errorParam) {
          throw new Error(`Google OAuth error: ${errorParam}`);
        }

        // Get the OAuth code from the callback URL
        const code = searchParams.get('code');
        
        // authenticateWithGoogle handles both cases:
        // - With code: calls backend to exchange code for tokens and set cookie
        // - Without code: checks if cookie is already set (backend processed callback server-side)
        await authenticateWithGoogle(code || undefined);
        
        // Verify authentication was successful by checking user info one more time
        // This ensures the cookie is actually set and working
        try {
          const userInfo = await authService.getCurrentUser();
          if ((userInfo as any)?.isExpected401) {
            throw new Error('Authentication verification failed - cookie not working');
          }
        } catch (verifyError) {
          console.error('Authentication verification failed:', verifyError);
          throw new Error('Google login completed but authentication cookie is not working. Please try again.');
        }
        
        showSnackbar('Successfully signed in with Google!', 'success');
        navigate('/', { replace: true });
      } catch (err) {
        console.error('Google OAuth callback error:', err);
        setError(err instanceof Error ? err.message : 'Failed to authenticate with Google');
        showSnackbar('Failed to sign in with Google', 'error');
        
        // Redirect to home page after a delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } finally {
        setLoading(false);
        // Clear the flag after callback is complete
        setGoogleCallbackFlow(false);
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate, authenticateWithGoogle, showSnackbar]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Completing Google sign-in...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Failed
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Current URL: {window.location.href}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Redirecting to home page...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return null;
};

export default GoogleCallback;
